import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";
import {
  CheckCircle,
  ShieldAlert,
  AlertOctagon,
  User,
  MapPin,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  Ban,
  Award,
} from "lucide-react";
import LiveRoomLauncher from "../LiveRoomLauncher";
import TrackLauncher from "../TrackLauncher";
import AlertImageGallery from "../AlertImageGallery";
import EvidencePin from "./EvidencePin";
import { typeIcons, typeColors, typeIconColor, VOTABLE_TYPES, formatTime } from "./alertMeta";

export default function AlertCard({
  alert,
  user,
  isAdmin,
  pulse,
  onResolve,
  onAssign,
  onDelete,
  onVote,
  onFocus,
}) {
  const { t } = useTranslation();
  const Icon = typeIcons[alert.alert_type] || ShieldAlert;

  const canResolve =
    user?.role === "system_admin" || user?.role === "govt_admin" || alert.user_id === user?.user_id;
  const canVote = onVote && VOTABLE_TYPES.has(alert.alert_type) && alert.user_id !== user?.user_id;

  const handleDelete = () => {
    if (!onDelete) return;
    if (window.confirm(t("alert_feed.delete_confirm"))) {
      onDelete(alert.alert_id);
    }
  };

  return (
    <div
      className={`alert-feed-item bg-white border p-3 rounded-sm hover:shadow-sm transition-all duration-300 ${pulse ? "border-blue-500 ring-2 ring-blue-300/50" : "border-gray-200 hover:border-gray-400"}`}
      data-testid={`alert-item-${alert.alert_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Icon className="w-4 h-4 shrink-0" style={{ color: typeIconColor[alert.alert_type] || "#6B7280" }} />
          <Badge className={`text-[10px] px-1.5 py-0 rounded-sm ${typeColors[alert.alert_type] || "bg-gray-100 text-gray-700"}`}>
            {alert.alert_type}
          </Badge>
          {alert.escalation_level === 1 && (
            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-orange-500 text-white hover:bg-orange-500" data-testid={`escalated-${alert.alert_id}`}>
              <AlertOctagon className="w-2.5 h-2.5 mr-0.5" /> {t("alert_feed.escalated")}
            </Badge>
          )}
          {alert.escalation_level >= 2 && (
            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-red-700 text-white hover:bg-red-700 animate-pulse" data-testid={`critical-${alert.alert_id}`}>
              <AlertOctagon className="w-2.5 h-2.5 mr-0.5" /> {t("alert_feed.critical")}
            </Badge>
          )}
          {alert.flagged_false && (
            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-gray-800 text-white hover:bg-gray-800" data-testid={`flagged-false-${alert.alert_id}`}>
              <Ban className="w-2.5 h-2.5 mr-0.5" /> {t("alert_feed.flagged_false")}
            </Badge>
          )}
        </div>
        <span className="text-[10px] text-gray-400 whitespace-nowrap font-body">
          {formatTime(alert.created_at, t)}
        </span>
      </div>
      <p className="text-sm text-gray-800 font-body mb-1 line-clamp-2">
        {alert.description}
      </p>
      {isAdmin && alert.has_evidence && <EvidencePin alertId={alert.alert_id} />}
      <AlertImageGallery alert={alert} user={user} />
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        <LiveRoomLauncher alert={alert} user={user} />
        <TrackLauncher alert={alert} user={user} />
      </div>
      {alert.address && (
        <div className="flex items-center gap-2 mb-1">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${alert.latitude},${alert.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-blue-700 font-body inline-flex items-center gap-1 group/addr min-w-0"
            title="Open in Google Maps"
            data-testid={`alert-address-${alert.alert_id}`}
          >
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="underline-offset-2 group-hover/addr:underline truncate">{alert.address}</span>
          </a>
          {onFocus && (
            <button
              type="button"
              onClick={() => onFocus(alert)}
              className="text-[10px] text-gray-500 hover:text-gray-900 hover:bg-gray-100 px-1.5 py-0.5 rounded-sm border border-gray-200 shrink-0 font-body"
              title="Center the map on this alert"
              data-testid={`alert-focus-${alert.alert_id}`}
            >
              {t("alert_feed.show_on_map")}
            </button>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 flex-wrap gap-1">
        <div className="flex items-center gap-2">
          <p className="text-[10px] text-gray-400 font-body">{alert.user_name}</p>
          {alert.sender_trust_score >= 80 && (
            <Badge className="text-[9px] px-1 py-0 rounded-sm bg-green-50 text-green-700 hover:bg-green-50 border border-green-200" title="High trust score — alerts from this user are highly verified" data-testid={`trust-high-${alert.alert_id}`}>
              <Award className="w-2.5 h-2.5 mr-0.5" /> {t("alert_feed.trusted_badge")}
            </Badge>
          )}
          {alert.sender_trust_score !== undefined && alert.sender_trust_score < 30 && (
            <Badge className="text-[9px] px-1 py-0 rounded-sm bg-gray-100 text-gray-600 hover:bg-gray-100 border border-gray-300" title="Low trust score — verify carefully" data-testid={`trust-low-${alert.alert_id}`}>
              {t("alert_feed.low_trust_badge")}
            </Badge>
          )}
          {alert.assigned_name && (
            <span className="text-[10px] text-blue-600 font-body flex items-center gap-0.5">
              <User className="w-2.5 h-2.5" /> {alert.assigned_name}
            </span>
          )}
          {(alert.true_count > 0 || alert.false_count > 0) && (
            <span className="text-[10px] text-gray-500 font-body flex items-center gap-1.5" data-testid={`votes-${alert.alert_id}`}>
              <span className="flex items-center gap-0.5 text-green-700"><ThumbsUp className="w-2.5 h-2.5" />{alert.true_count || 0}</span>
              <span className="flex items-center gap-0.5 text-red-700"><ThumbsDown className="w-2.5 h-2.5" />{alert.false_count || 0}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 flex-wrap">
          {canVote && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onVote(alert.alert_id, "true")}
                className={`h-6 text-[10px] rounded-sm px-1.5 ${alert.my_vote === "true" ? "text-green-800 bg-green-100" : "text-green-700 hover:bg-green-50"}`}
                disabled={!!alert.my_vote}
                title={alert.my_vote ? `You voted ${alert.my_vote}` : "Verify this alert (must be within 500 m)"}
                data-testid={`vote-true-${alert.alert_id}`}
              >
                <ThumbsUp className="w-3 h-3 mr-0.5" /> {t("alert_feed.vote_true")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onVote(alert.alert_id, "false")}
                className={`h-6 text-[10px] rounded-sm px-1.5 ${alert.my_vote === "false" ? "text-red-800 bg-red-100" : "text-red-700 hover:bg-red-50"}`}
                disabled={!!alert.my_vote}
                title={alert.my_vote ? `You voted ${alert.my_vote}` : "Mark as false (must be within 500 m)"}
                data-testid={`vote-false-${alert.alert_id}`}
              >
                <ThumbsDown className="w-3 h-3 mr-0.5" /> {t("alert_feed.vote_false")}
              </Button>
            </>
          )}
          {canResolve && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onResolve(alert.alert_id)}
              className="h-6 text-[10px] text-green-700 hover:text-green-800 hover:bg-green-50 rounded-sm px-2"
              data-testid={`resolve-alert-${alert.alert_id}`}
            >
              <CheckCircle className="w-3 h-3 mr-1" /> {t("alert_feed.resolve")}
            </Button>
          )}
          {onAssign && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onAssign(alert.alert_id)}
              className="h-6 text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-sm px-2"
              data-testid={`assign-alert-${alert.alert_id}`}
            >
              <User className="w-3 h-3 mr-1" /> {t("alert_feed.assign")}
            </Button>
          )}
          {isAdmin && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              className="h-6 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm px-2"
              data-testid={`delete-alert-${alert.alert_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
